// ============================================================
// TotalEnrollmentGender — replaces app-total-enroollment-gender
// Loads CSV 10207.csv, aggregates male/female by academic year
// ============================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Papa from 'papaparse';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  parseCsvToObjects,
  sumField,
  getMngTypeOnUserType,
  calculateFilteredData,
} from '../../shared/utils';
import { RootState } from '../../../../store';
import ChartPanelWrapper from '../../../../shared/graph/graphPanelWrapper';
import SharedFilter from '../../../../shared/filter/sharedFilter';
import { FilterState } from '../../../../shared/filter/filterTypes';
import '../../../../shared/filter/sharedFilter.scss';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import '../../dashboard.scss';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CSV_URL = 'https://d3ijhv7dn0xr3b.cloudfront.net/10207.csv';
const YEARS = ['2020-21', '2021-22', '2022-23'];
const COLORS = ['#5970ca', '#a1edec'];

interface GenderRow {
  Acad_yr: string;
  male: number;
  female: number;
}

const TotalEnrollmentGender: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const usertypeid = s.usertypeid ?? '';
  const emisUsertype1 = s.emisUsertype1 ?? '';
  const districtId = String(s.districtId ?? '');
  const edu_district_id = String(s.edu_district_id ?? '');
  const blockId = String(s.blockId ?? '');

  const [overallData, setOverallData] = useState<any[]>([]);
  const [finalList, setFinalList] = useState<GenderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  const [filterState, setFilterState] = useState<FilterState | null>(null);

  useEffect(() => {
    Papa.parse(CSV_URL, {
      download: true,
      complete: (results) => {
        let rows = parseCsvToObjects(results.data as string[][]);
        if (usertypeid === '24' && ['5', '6'].includes(emisUsertype1)) {
          rows = rows.filter((x) => String(x.manage_id) === '2');
        }
        setOverallData(rows);
        setLoading(false);
      },
      error: () => setLoading(false),
    });
  }, [usertypeid, emisUsertype1]);

  useEffect(() => {
    if (overallData.length === 0) return;

    // Apply shared filter
    let data = overallData;
    if (filterState) {
      data = calculateFilteredData(
        overallData,
        filterState as unknown as Record<string, boolean>
      );
    }

    const geoType = getMngTypeOnUserType(usertypeid, emisUsertype1);
    let filtered = data;

    if (typeof geoType === 'number' || Array.isArray(geoType)) {
      const ids = ([] as number[]).concat(geoType as any).map(String);
      filtered = data.filter((x) => ids.includes(String(x.manage_id)));
    } else if (geoType === 'district') {
      filtered = data.filter((x) => String(x.district_id) === districtId);
    } else if (geoType === 'edu_district') {
      filtered = data.filter((x) => String(x.edu_dist_id) === edu_district_id);
    } else if (geoType === 'block') {
      filtered = data.filter((x) => String(x.block_id) === blockId);
    }

    const rows: GenderRow[] = YEARS.map((yr) => {
      const yearData = filtered.filter((x) => x.Acad_yr === yr);
      return {
        Acad_yr: yr,
        male: sumField(
          yearData.filter((x) => x.Gender === '1'),
          'Students'
        ),
        female: sumField(
          yearData.filter((x) => x.Gender === '2'),
          'Students'
        ),
      };
    });
    queueMicrotask(() => setFinalList(rows));
  }, [
    overallData,
    filterState,
    usertypeid,
    emisUsertype1,
    districtId,
    edu_district_id,
    blockId,
  ]);

  const handleFilterChange = useCallback((filters: FilterState) => {
    setFilterState(filters);
  }, []);

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  // Build chart datasets based on filter state — only show checked genders
  const datasets = [];
  if (!filterState || filterState.male) {
    datasets.push({
      label: 'Male',
      data: finalList.map((r) => r.male),
      backgroundColor: COLORS[0],
    });
  }
  if (!filterState || filterState.female) {
    datasets.push({
      label: 'Female',
      data: finalList.map((r) => r.female),
      backgroundColor: COLORS[1],
    });
  }

  const chartData = {
    labels: YEARS,
    datasets,
  };

  const options = {
    responsive: true,
    plugins: { legend: { position: 'top' as const } },
    scales: { y: { title: { display: true, text: 'Enrollment Number' } } },
  };

  const chartContent = (
    <>
      <div className="d-flex justify-content-center align-items-center">
        <p className="text-muted small mb-2">
          (Hover on the Graph to view Numbers)
        </p>
      </div>
      <Bar data={chartData} options={options} />
    </>
  );

  const gridContent = (
    <div className="comparative-grid mt-3">
      <div className="mb-2">
        <input
          type="text"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
          className="global-search-input"
        />
      </div>
      <DataTable
        value={finalList}
        sortMode="single"
        size="small"
        stripedRows
        emptyMessage="No data found"
        globalFilter={globalFilter}
        globalFilterFields={['Acad_yr', 'male', 'female']}
      >
        <Column field="Acad_yr" header="Academic Year" sortable />
        {(!filterState || filterState.male) && (
          <Column
            field="male"
            header="Male"
            sortable
            body={(row) => row.male.toLocaleString('en-IN')}
          />
        )}
        {(!filterState || filterState.female) && (
          <Column
            field="female"
            header="Female"
            sortable
            body={(row) => row.female.toLocaleString('en-IN')}
          />
        )}
      </DataTable>
    </div>
  );

  return (
    <div className="chart-with-filter">
      <SharedFilter showFilters={['4']} onFilterChange={handleFilterChange} />
      <ChartPanelWrapper
        title=""
        chartContent={chartContent}
        gridContent={gridContent}
        loading={loading}
      />
    </div>
  );
};

export default TotalEnrollmentGender;
